// Firebase Storage service for file uploads and management
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";
import {storage, auth} from "./config";

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
}

export interface CharacterSheetUpload {
  file: File;
  characterId: string;
}

export interface StorageError {
  code: string;
  message: string;
}

/**
 * Upload character sheet file
 */
export const uploadCharacterSheet = async ({
  file,
  characterId,
}: CharacterSheetUpload): Promise<string> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User must be authenticated to upload files");
    }

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Only PDF and image files (JPEG, PNG) are allowed");
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error("File size must be less than 10MB");
    }

    // Create storage reference
    const fileName = `character-sheets/${characterId}/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);

    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);

    return downloadURL;
  } catch (error) {
    // Error uploading character sheet - rethrow for handling by caller
    throw error;
  }
};

/**
 * Delete character sheet file
 */
export const deleteCharacterSheet = async (fileUrl: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User must be authenticated to delete files");
    }

    // Extract file path from URL
    const url = new URL(fileUrl);
    let filePath: string | null = null;

    // Handle emulator URLs (format: /v0/b/bucket/o/path?alt=media)
    const emulatorMatch = url.pathname.match(/\/o\/(.+?)(?:\?|$)/);
    if (emulatorMatch) {
      filePath = decodeURIComponent(emulatorMatch[1]);
    } else {
      // Handle production URLs (format: https://storage.googleapis.com/bucket/path)
      const productionMatch = url.pathname.match(/^\/([^/]+)\/(.+)$/);
      if (productionMatch) {
        filePath = productionMatch[2]; // Skip the bucket name, just get the path
      }
    }

    if (!filePath) {
      throw new Error("Invalid file URL format");
    }

    const fileRef = ref(storage, filePath);

    await deleteObject(fileRef);
  } catch (error) {
    // Error deleting character sheet - rethrow for handling by caller
    throw error;
  }
};

/**
 * Get all character sheets for a character
 */
export const getCharacterSheets = async (
  characterId: string
): Promise<string[]> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User must be authenticated to access files");
    }

    const folderRef = ref(storage, `character-sheets/${characterId}`);
    const result = await listAll(folderRef);

    const urls = await Promise.all(
      result.items.map(async (item) => {
        return await getDownloadURL(item);
      })
    );

    return urls;
  } catch (error) {
    // Error getting character sheets - rethrow for handling by caller
    throw error;
  }
};

/**
 * Replace character sheet (delete old, upload new)
 */
export const replaceCharacterSheet = async ({
  file,
  characterId,
  oldFileUrl,
}: CharacterSheetUpload & {oldFileUrl?: string}): Promise<string> => {
  try {
    // Delete old file if provided
    if (oldFileUrl) {
      await deleteCharacterSheet(oldFileUrl);
    }

    // Upload new file
    const newFileUrl = await uploadCharacterSheet({
      file,
      characterId,
    });

    return newFileUrl;
  } catch (error) {
    // Error replacing character sheet - rethrow for handling by caller
    throw error;
  }
};

/**
 * Validate file before upload
 */
export const validateCharacterSheetFile = (
  file: File
): {isValid: boolean; error?: string} => {
  // Check file type
  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
  ];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: "Only PDF and image files (JPEG, PNG) are allowed",
    };
  }

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: "File size must be less than 10MB",
    };
  }

  return {isValid: true};
};

/**
 * Get file extension from URL
 */
export const getFileExtension = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const extension = pathname.split(".").pop()?.toLowerCase();
    return extension || "";
  } catch {
    return "";
  }
};

/**
 * Get file name from URL
 */
export const getFileName = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split("/").pop() || "";
    return decodeURIComponent(fileName);
  } catch {
    return "";
  }
};
